/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type Maybe from 'graphql/tsutils/Maybe';

import Imgix from 'js/components/Imgix';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography, breakpoints, useTheme } from '@coursera/cds-core';

import type {
  CtaFragment,
  EntWebsitePartnerListFragment,
  ImageFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import BenefitList from 'bundles/ent-website/components/BenefitList';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import Image from 'bundles/ent-website/contentful/core/Image';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

import _t from 'i18n!nls/ent-website';

const styles = {
  container: css`
    border-radius: 8px;
    overflow: hidden;
    margin: var(--cds-spacing-600) 0;
    background-color: var(--cds-color-grey-25);
    position: relative;

    ul {
      display: block;
      padding: 0 !important;
    }

    li {
      margin-bottom: var(--cds-spacing-200);

      span {
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        letter-spacing: -0.48px;
      }
    }
  `,
  partnerList: css`
    overflow: hidden;
    justify-content: center;
    align-items: center;
    display: flex;

    ul {
      list-style-type: none;
      position: relative;
      padding-inline-start: 0;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      margin-top: var(--cds-spacing-150);
      margin-bottom: 0;
      left: 1px;
      top: 1px;
      margin: 0 !important;
      padding: 0 !important;
    }

    ul li {
      overflow: hidden;
      aspect-ratio: 1;
      margin-bottom: 0;
      display: flex;
      padding: var(--cds-spacing-300);
      align-items: center;
      justify-content: center;
      border-right: 1px solid var(--cds-color-grey-50);
      border-bottom: 1px solid var(--cds-color-grey-50);

      ${breakpoints.down('sm')} {
        padding: var(--cds-spacing-150);
      }
    }

    ul li img {
      max-width: 100%;
      object-fit: cover;
      max-height: 100%;
      ${breakpoints.down('xs')} {
        max-height: 48px;
      }
    }

    ${breakpoints.down('sm')} {
      height: 100%;

      ul {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-400);

      ul {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  `,
  leftContentContainer: (_: Theme, leftBackgroundColor: Maybe<string>, leftFontColor?: Maybe<string>) => css`
    position: relative;
    padding: var(--cds-spacing-600);
    background-color: ${leftBackgroundColor ?? 'inherit'};

    h1,
    h2,
    h3,
    h4,
    h5,
    ul,
    li,
    p {
      color: ${leftFontColor ?? 'inherit'} !important;
    }

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400);
    }

    ${breakpoints.down('xs')} {
      width: 100%;
      padding: var(--cds-spacing-300) var(--cds-spacing-300) var(--cds-spacing-300) var(--cds-spacing-300);
    }
  `,
  leftContentOnly: css`
    ${breakpoints.down('xs')} {
      padding-bottom: var(--cds-spacing-400);
    }
  `,
  rightContentContainer: (_: Theme, rightBackgroundColor: Maybe<string>, rightFontColor?: Maybe<string>) => css`
    position: relative;
    width: 100%;
    padding: var(--cds-spacing-600) var(--cds-spacing-800);
    background-color: ${rightBackgroundColor ?? 'inherit'};

    h1,
    h2,
    h3,
    h4,
    h5,
    ul,
    li,
    span,
    p,
    svg {
      color: ${rightFontColor ?? 'inherit'} !important;
    }

    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-400) var(--cds-spacing-400) var(--cds-spacing-400);
    }
  `,
  leftContent: css`
    position: relative;

    ${breakpoints.down('xs')} {
      padding-right: 0;
    }
  `,
  rightContent: css`
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;

    ${breakpoints.down('xs')} {
      padding-left: 0;
    }
  `,
  cta: css`
    ${breakpoints.down('xs')} {
      max-width: 100%;
    }
  `,
  coverImage: css`
    position: absolute;
    overflow: visible;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
  `,
  benefitList: css`
    margin-bottom: 0 !important;

    svg {
      margin-inline-end: var(--cds-spacing-200) !important;
    }

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
  logo: css`
    object-fit: contain;
    width: 200px;
    object-position: top left;
    margin-bottom: var(--cds-spacing-300);
  `,
  headline: css`
    font-size: 32px;
    font-weight: 600;
    line-height: 100%;
    letter-spacing: -0.96px;
    margin-bottom: var(--cds-spacing-100);
  `,
  body: css`
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.48px;
    margin-bottom: var(--cds-spacing-400);
    flex: 1;
  `,
  rightImage: css`
    object-fit: cover;
    object-position: center;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    right: 0;

    ${breakpoints.down('sm')} {
      position: initial;
    }
  `,
};

export type Props = {
  headline?: Maybe<string>;
  body?: Maybe<string>;
  cta?: Maybe<CtaFragment>;
  logo?: Maybe<ImageFragment>;
  coverImage?: Maybe<ImageFragment>;
  leftBackgroundColor?: Maybe<string>;
  rightBackgroundColor?: Maybe<string>;
  leftFontColor?: Maybe<string>;
  rightFontColor?: Maybe<string>;
  rightContentType?: Maybe<string>;
  image?: Maybe<ImageFragment>;
  partnerList?: Maybe<EntWebsitePartnerListFragment>;
  bulletPoints?: Maybe<Array<Maybe<{ title?: Maybe<string> }>>>;
  module: BlockLevelModule;
};

type PromoUnitContainerProps = {
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
};

const CoverImage = ({ url }: { url: string }) => (
  <Imgix src={url} alt={_t('Promo Unit Cover Image')} css={styles.coverImage} />
);

const PromoUnitContainer = ({
  leftContent,
  rightContent,
  coverImage,
  leftBackgroundColor,
  leftFontColor,
}: Props & PromoUnitContainerProps) => {
  const theme = useTheme();

  return (
    <Grid container css={styles.container}>
      {leftContent && (
        <Grid
          item
          sm={rightContent ? 6 : 12}
          css={styles.leftContentContainer(theme, leftBackgroundColor, leftFontColor)}
        >
          {coverImage?.url && <CoverImage url={coverImage.url} />}
          {leftContent}
        </Grid>
      )}
      {rightContent}
    </Grid>
  );
};

const EnterpriseArticlePromoUnit = ({
  headline,
  body,
  cta,
  logo,
  coverImage,
  rightContentType,
  image,
  bulletPoints,
  partnerList,
  leftBackgroundColor,
  leftFontColor,
  rightBackgroundColor,
  rightFontColor,
  module,
}: Props) => {
  const theme = useTheme();
  const hasBulletPoints = bulletPoints && bulletPoints?.length > 0;
  const hasPartnerList = partnerList?.partnersCollection && partnerList?.partnersCollection?.items?.length > 0;
  const hasImage = !!image?.url;

  const hasRightContent = hasBulletPoints || hasPartnerList || hasImage;

  const renderRightContent = React.useCallback<() => $TSFixMe>(() => {
    if (!hasRightContent) return null;
    let el = null;

    switch (rightContentType) {
      case 'Image':
        if (!image?.url) return null;
        return (
          <Grid
            sm={6}
            css={css`
              position: relative;
            `}
          >
            <img src={image?.url} alt={image?.description ?? _t('Promo Unit Image')} css={styles.rightImage} />
          </Grid>
        );
      case 'Bullet Points':
        el = <BenefitList style={styles.benefitList} items={bulletPoints?.map((bp) => bp?.title)} />;
        break;
      case 'Partner Grid':
        el = (
          <div css={[styles.partnerList]} data-testid="partner_grid">
            <ul>
              {partnerList?.partnersCollection?.items.map((partner) =>
                partner?.logo ? (
                  <li key={partner?.slug}>
                    <Image content={partner.logo} width="auto" />
                  </li>
                ) : null
              )}
            </ul>
          </div>
        );
        break;
      case 'None':
        return null;
      default:
        break;
    }

    return (
      <Grid sm={6} css={styles.rightContentContainer(theme, rightBackgroundColor, rightFontColor)}>
        <Grid css={styles.rightContent}>{el}</Grid>
      </Grid>
    );
  }, [
    bulletPoints,
    hasRightContent,
    image?.description,
    image?.url,
    partnerList?.partnersCollection?.items,
    rightBackgroundColor,
    rightContentType,
    rightFontColor,
    theme,
  ]);

  return (
    <PromoUnitContainer
      coverImage={coverImage}
      leftBackgroundColor={leftBackgroundColor}
      rightBackgroundColor={rightBackgroundColor}
      leftFontColor={leftFontColor}
      rightFontColor={rightFontColor}
      leftContent={
        <Grid
          container
          direction="column"
          css={[
            styles.leftContent,
            hasRightContent && rightContentType !== 'None' ? undefined : styles.leftContentOnly,
          ]}
        >
          {logo?.url && <Imgix src={logo?.url} alt={_t('Coursera Logo')} css={styles.logo} />}

          <Typography variant="d2semibold" css={styles.headline}>
            {headline}
          </Typography>
          <Markdown source={body} module={module} />
          {cta?.ctaTarget && cta?.ctaText && (
            <ContentfulButton
              component="a"
              href={cta?.ctaTarget}
              position={0}
              module={module}
              target={cta?.shouldOpenInNewWindow ? '_blank' : undefined}
              variant={cta?.invert ? 'primaryInvert' : 'primary'}
              styles={styles.cta}
              role="button"
            >
              {cta.ctaText}
            </ContentfulButton>
          )}
        </Grid>
      }
      rightContent={renderRightContent()}
      module={module}
    />
  );
};

export default EnterpriseArticlePromoUnit;
